"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const SmokeyCursor = dynamic(() => import("@/components/lightswind/smokey-cursor"), {
  ssr: false,
  loading: () => null,
});

/**
 * Desktop-only smokey-cursor trail, confined to the hero area.
 *
 * The underlying SmokeyCursor canvas uses `position: fixed` internally,
 * so without a scope guard it would trail the cursor across every
 * section of the page. We unmount it the moment the hero leaves the
 * viewport so the rest of the homepage stays clean.
 *
 * Look-up contract: the host hero must mark its root element with
 * `data-hero-bounds`. We observe that element; when it leaves the
 * viewport the smokey canvas is unmounted (no hidden animation loop
 * chewing CPU off-screen). If the attribute isn't found we fall back
 * to a simple "scrollY < innerHeight" gate so legacy heroes still work.
 *
 * Cost: mounted 2s after first paint (never during LCP window).
 */
export default function HeroDesktopEffects() {
  const [mount, setMount] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    if (!isDesktop) return;
    const timer = setTimeout(() => setMount(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!mount) return;

    const target = document.querySelector<HTMLElement>("[data-hero-bounds]");
    if (target && typeof IntersectionObserver !== "undefined") {
      const obs = new IntersectionObserver(
        (entries) => {
          for (const e of entries) setVisible(e.isIntersecting);
        },
        { threshold: 0 }
      );
      obs.observe(target);
      return () => obs.disconnect();
    }

    // Fallback: pure scroll position. Hides smokey once the user is
    // roughly past the first viewport height.
    const onScroll = () => {
      setVisible(window.scrollY < window.innerHeight * 0.95);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mount]);

  if (!mount || !visible) return null;
  return <SmokeyCursor />;
}
